import React from "react";
import ReactECharts from "echarts-for-react";
import { toTimestamp, formatDateFr } from "../../utils/echartsSeries.js";
import { HORIZON_COLORS } from "../../constants.js";

function diffDays(dateA, dateB) {
  return Math.round((new Date(dateA).getTime() - new Date(dateB).getTime()) / 86400000);
}

export default function PhenoChart({ data }) {
  const byGroup = new Map();
  data.forEach((row) => {
    if (!byGroup.has(row.YearGroup)) byGroup.set(row.YearGroup, []);
    byGroup.get(row.YearGroup).push(row);
  });

  const series = Array.from(byGroup.entries()).map(([group, rows]) => {
    const sorted = [...rows].sort((a, b) => (a.date_calendaire > b.date_calendaire ? 1 : -1));
    const points = sorted.map((r) => {
      const showLabel = r.is_reco && String(group) === "2050";
      const point = { value: [toTimestamp(r.date_calendaire), r.GDD_VF] };
      if (showLabel) {
        point.label = {
          show: true,
          formatter: r.Libelle_SP,
          position: "left",
          fontWeight: "bold",
        };
      }
      return point;
    });

    return {
      name: String(group),
      type: "line",
      smoothed: true,
      symbol: "circle",
      symbolSize: 8,
      label: { show: false },
      lineStyle: { width: 2.5, color: HORIZON_COLORS[group] || "#888" },
      itemStyle: { color: HORIZON_COLORS[group] || "#888" },
      data: points,
    };
  });

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", label: { show: false } },
      formatter: (params) => {
        const date = formatDateFr(params[0].value[0]);
        let html = `${date}<br/>`;
        params.forEach((p) => {
          html += `${p.marker} ${p.seriesName}<br/>`;
        });
        return html;
      },
    },
    legend: { bottom: 0, textStyle: { fontSize: 16 } },
    grid: { left: 20, right: 20, top: 20, bottom: 60, containLabel: true },
    xAxis: { type: "time", axisLabel: { formatter: formatDateFr } },
    yAxis: { show: false },
    series,
  };

  // ---- tableau : une ligne par stade "reconnaissable" (is_reco), colonnes
  // 2020 (date) / 2035 / 2050 (écart en jours vs 2020) ----
  const recoRows = data.filter((r) => r.is_reco);
  const byStage = new Map();
  recoRows.forEach((r) => {
    if (!byStage.has(r.Libelle_SP)) byStage.set(r.Libelle_SP, {});
    byStage.get(r.Libelle_SP)[r.YearGroup] = r.date_calendaire;
  });

  const stagesTries = [...byStage.entries()].sort((a, b) => {
    const da = a[1][2020] ?? Object.values(a[1])[0];
    const db = b[1][2020] ?? Object.values(b[1])[0];
    return new Date(da).getTime() - new Date(db).getTime();
  });

  return (
    <div className="diagoeau-pheno-layout" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{ flex: "2 1 0" }}>
        <ReactECharts option={option} style={{ height: 500 }} notMerge lazyUpdate />
      </div>

      {stagesTries.length > 0 && (
        <div style={{ flex: "1 1 0", overflowX: "auto" }}>
          <table className="diagoeau-data-table diagoeau-table-centered">
            <thead>
              <tr>
                <th></th>
                <th>2020</th>
                <th>2035</th>
                <th>2050</th>
              </tr>
            </thead>
            <tbody>
              {stagesTries.map(([label, dates]) => {
                const d2020 = dates[2020];
                const d2035 = dates[2035];
                const d2050 = dates[2050];
                const ecart2035 = d2020 && d2035 ? diffDays(d2035, d2020) : null;
                const ecart2050 = d2020 && d2050 ? diffDays(d2050, d2020) : null;
                return (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{d2020 ? formatDateFr(d2020) : "—"}</td>
                    <td>{ecart2035 == null ? "—" : `${ecart2035 > 0 ? "+" : ""}${ecart2035} j`}</td>
                    <td>{ecart2050 == null ? "—" : `${ecart2050 > 0 ? "+" : ""}${ecart2050} j`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}